import { getScopedProxyQueryKey } from "@/transport"
import { useQuery } from "@tanstack/react-query"

import { fetchSofiaProxy } from "@/lib/sofiaProxyRequest"

import {
  githubReceiptWindowSchema,
  scopedGithubReceiptsSchema,
} from "../utils/githubReceiptSchemas"
import {
  calculateRelativeDashboardDateRange,
  type RelativeDashboardDateRange,
} from "../utils/relativeDashboardDateRange"

export class GithubReceiptRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
  }
}

export class GithubReceiptRangeError extends Error {}

export function useGithubReceipts(
  scope: {
    organizationSlug: string
    organizationId: string | undefined
    workspaceId: string
    userId: string | undefined
  },
  dates: { start_date: string; end_date: string },
  enabled: boolean,
  relative?: RelativeDashboardDateRange | null,
) {
  return useQuery({
    queryKey: [
      ...getScopedProxyQueryKey("sofia-github-receipts", scope),
      relative ? { relative } : { dates },
    ],
    enabled:
      enabled &&
      Boolean(
        scope.organizationSlug &&
        scope.organizationId &&
        scope.workspaceId &&
        scope.userId,
      ),
    queryFn: async ({ signal }) => {
      const range = relative
        ? calculateRelativeDashboardDateRange(relative)
        : null
      const requestDates = range
        ? {
            start_date: range.start.toISOString(),
            end_date: range.end.toISOString(),
          }
        : dates
      if (!githubReceiptWindowSchema.safeParse(requestDates).success) {
        throw new GithubReceiptRangeError(
          "Select a dashboard date range of at most 93 days.",
        )
      }
      const response = await fetchSofiaProxy(
        `/api/sofia/github-receipts/${encodeURIComponent(scope.organizationSlug)}`,
        {
          organizationSlug: scope.organizationSlug,
          method: "POST",
          signal,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            workspace: scope.workspaceId,
            ...requestDates,
          }),
        },
      )
      if (!response.ok) {
        throw new GithubReceiptRequestError(
          response.status === 403
            ? "Check internal operator and organization admin access, and this workspace's GitHub connection."
            : response.status === 401
              ? "Your session could not be authenticated. Refresh your session to continue."
              : response.status === 404
                ? "GitHub receipts are unavailable here. Check the source rollout and workspace access."
                : "GitHub receipts are unavailable. Retry shortly.",
          response.status,
        )
      }
      const value = scopedGithubReceiptsSchema.parse(await response.json())
      if (
        value.clerk_organization_id !== scope.organizationId ||
        value.workspace_id !== scope.workspaceId ||
        Date.parse(value.start_date) !== Date.parse(requestDates.start_date) ||
        Date.parse(value.end_date) !== Date.parse(requestDates.end_date)
      ) {
        throw new Error("GitHub receipts changed scope. Refresh to continue.")
      }
      return value
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  })
}
